import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Button,
  EmptyState,
  Input,
  ReportTable,
  type ReportTableColumn,
  type ReportTablePaginationState,
  type ReportTableSort,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  VirtualizedMultiSelectCombobox,
} from '@roger/ui-components';
import { ArrowLeft, Download } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { ReportLink } from '../components/reporting/ReportLink';
import { ReportMultiSelectFilter } from '../components/reporting/ReportMultiSelectFilter';
import { ReportPopulationControls } from '../components/reporting/ReportPopulationControls';
import { buildReportPopulationQuery } from '../components/reporting/reportPopulation';
import {
  getStoredIncludeImportedPatients,
  setStoredIncludeImportedPatients,
} from '../utils/populationPreferences';
import { PermissionSlug } from '../constants/featureFlags';
import { useDebounce } from '../hooks/useDebounce';
import { useReportsBackHref } from '../reporting/backHref';
import {
  exportPastDueVisitsReportReportsPastDueVisitsExportGet,
  getPastDueVisitsReportOptionsReportsPastDueVisitsOptionsGet,
  getPastDueVisitsReportReportsPastDueVisitsGet,
} from '../services/api/sdk.gen';
import type {
  PastDueVisitsOption,
  PastDueVisitsReportResult,
  PastDueVisitsReportRow,
  PastDueVisitsSortBy,
  PastDueVisitsTaskCategory,
  PastDueVisitsSortDirection,
} from '../services/api/types.gen';
import { useStore } from '../store';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import { downloadResponseAsFile } from '../utils/downloadFile';
import {
  REPORT_PAGE_SIZE_OPTIONS,
  coerceReportPageSize,
  formatReportDate,
  formatReportEmptyValue,
  readDelimitedParam,
  readOptionalChoiceParam,
  readPositiveIntegerParam,
} from '../utils/reportDisplay';
import ReportsPermissionAlert from './ReportsPermissionAlert';

const VALID_SORTS: PastDueVisitsSortBy[] = [
  'patient',
  'mrn',
  'clinic',
  'task_name',
  'discipline',
  'assigned_clinician',
  'status',
  'target_date',
  'visit_date',
  'days_past_due',
];
const VALID_DIRECTIONS: PastDueVisitsSortDirection[] = ['asc', 'desc'];
type PastDueTaskType = 'both' | PastDueVisitsTaskCategory;

function readTaskType(value: string | null): PastDueTaskType {
  return value === 'visit' || value === 'order' || value === 'misc'
    ? value
    : 'both';
}

function categoriesForTaskType(
  value: PastDueTaskType
): PastDueVisitsTaskCategory[] {
  return value === 'both' ? ['visit', 'order', 'misc'] : [value];
}

const columns: ReportTableColumn<PastDueVisitsReportRow>[] = [
  {
    id: 'patient',
    header: 'Patient',
    accessor: 'patient' as const,
    render: (_value: unknown, row: PastDueVisitsReportRow) => (
      <ReportLink to={`/patients/${row.patient_id}`}>{row.patient}</ReportLink>
    ),
    enableSorting: true,
    minWidth: 190,
  },
  {
    id: 'mrn',
    header: 'MRN',
    accessor: (row: PastDueVisitsReportRow) => formatReportEmptyValue(row.mrn),
    enableSorting: true,
    minWidth: 120,
  },
  {
    id: 'clinic',
    header: 'Clinic',
    accessor: (row: PastDueVisitsReportRow) =>
      formatReportEmptyValue(row.clinic),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'task_name',
    header: 'Task Name',
    accessor: 'task_name' as const,
    enableSorting: true,
    minWidth: 210,
  },
  {
    id: 'discipline',
    header: 'Discipline',
    accessor: 'discipline' as const,
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'assigned_clinician',
    header: 'Assigned Clinician',
    accessor: 'assigned_clinician' as const,
    enableSorting: true,
    minWidth: 200,
  },
  {
    id: 'status',
    header: 'Status',
    accessor: 'status_label' as const,
    enableSorting: true,
    minWidth: 210,
  },
  {
    id: 'target_date',
    header: 'Target Date',
    accessor: (row: PastDueVisitsReportRow) =>
      formatReportDate(row.target_date),
    enableSorting: true,
    minWidth: 140,
  },
  {
    id: 'visit_date',
    header: 'Visit Date',
    accessor: (row: PastDueVisitsReportRow) => formatReportDate(row.visit_date),
    enableSorting: true,
    minWidth: 140,
  },
  {
    id: 'days_past_due',
    header: 'Days Past Due',
    accessor: 'days_past_due' as const,
    align: 'right' as const,
    enableSorting: true,
    minWidth: 150,
  },
];

function readSort(value: string | null): PastDueVisitsSortBy | undefined {
  return readOptionalChoiceParam(value, VALID_SORTS);
}

function readDirection(
  value: string | null
): PastDueVisitsSortDirection | undefined {
  return readOptionalChoiceParam(value, VALID_DIRECTIONS);
}

function readFilterQuery(value: string): string[] | null {
  const values = readDelimitedParam(value);
  return values.length > 0 ? values : null;
}

function parseDaysInput(value: string): number | null {
  if (!value) return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed)) return null;
  return Math.max(1, parsed);
}

function formatDaysInput(value: number | null): string {
  return value === null ? '' : String(value);
}

function StatusFilter({
  value,
  options,
  onChange,
}: {
  value: string;
  options: PastDueVisitsOption[];
  onChange: (value: string) => void;
}) {
  const optionById = useMemo(
    () => new Map(options.map(option => [option.id, option])),
    [options]
  );
  const rawSelectedValues = useMemo(() => readDelimitedParam(value), [value]);
  const defaultValues = useMemo(
    () => options.map(option => option.id),
    [options]
  );
  const selectedValues = useMemo(
    () =>
      rawSelectedValues.length > 0
        ? rawSelectedValues.filter(optionId => optionById.has(optionId))
        : defaultValues,
    [defaultValues, optionById, rawSelectedValues]
  );
  const comboboxOptions = useMemo(
    () => options.map(option => ({ value: option.id, label: option.label })),
    [options]
  );
  const defaultLabels = options.map(option => option.label);
  const selectedLabels = selectedValues.map(
    optionId => optionById.get(optionId)?.label ?? optionId
  );
  const visibleLabels =
    selectedLabels.length > 0
      ? selectedLabels
      : rawSelectedValues.length > 0
        ? ['No allowed statuses']
        : defaultLabels;
  const ariaValue = visibleLabels.join(', ') || 'Select statuses';
  const labelId = 'past-due-visits-status-filter-label';

  return (
    <div
      role='group'
      aria-labelledby={labelId}
      className='space-y-1 text-sm font-medium'
    >
      <span id={labelId}>Status</span>
      <VirtualizedMultiSelectCombobox
        options={comboboxOptions}
        value={selectedValues}
        onValueChange={nextValue => {
          const hasDefaultSelection =
            defaultValues.length > 0 &&
            nextValue.length === defaultValues.length &&
            defaultValues.every(optionId => nextValue.includes(optionId));
          onChange(hasDefaultSelection ? '' : nextValue.join(','));
        }}
        placeholder={ariaValue}
        searchPlaceholder='Search status...'
        buttonClassName='w-full'
        ariaLabel={`Status: ${ariaValue}`}
      />
    </div>
  );
}

export default function PastDueVisitsReport() {
  const [searchParams, setSearchParams] = useSearchParams();
  const reportsBackHref = useReportsBackHref();
  const { hasPermission, showError } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
      showError: state.showError,
    }))
  );

  const [options, setOptions] = useState<{
    asOf: string;
    clinics: PastDueVisitsOption[];
    disciplines: PastDueVisitsOption[];
    assignedClinicians: PastDueVisitsOption[];
    statuses: PastDueVisitsOption[];
  }>({
    asOf: '',
    clinics: [],
    disciplines: [],
    assignedClinicians: [],
    statuses: [],
  });
  const [result, setResult] = useState<PastDueVisitsReportResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const asOf = searchParams.get('as_of') || options.asOf;
  const clinic = searchParams.get('clinic') || '';
  const taskType = readTaskType(searchParams.get('category'));
  const visibleTaskType = taskType === 'misc' ? 'both' : taskType;
  const discipline = searchParams.get('discipline') || '';
  const clinician = searchParams.get('clinician') || '';
  const status = searchParams.get('status') || '';
  const targetFrom = searchParams.get('target_from') || '';
  const targetTo = searchParams.get('target_to') || '';
  const includeTestRows = searchParams.get('include_test_rows') === 'true';
  const includeInactiveEpisodes =
    searchParams.get('include_inactive_episodes') === 'true';
  const includeImportedPatientsParam = searchParams.get(
    'include_imported_patients'
  );
  const includeImportedPatients =
    includeImportedPatientsParam !== null
      ? includeImportedPatientsParam === 'true'
      : getStoredIncludeImportedPatients();
  // Default on: the URL only carries the param when missed visits are hidden.
  const includeMissedVisits =
    searchParams.get('include_missed_visits') !== 'false';
  const settledDaysMin = formatDaysInput(
    parseDaysInput(searchParams.get('days_min') || '')
  );
  const settledDaysMax = formatDaysInput(
    parseDaysInput(searchParams.get('days_max') || '')
  );
  const settledQ = searchParams.get('q') || '';
  const [searchInput, setSearchInput] = useState(settledQ);
  const [daysMinInput, setDaysMinInput] = useState(settledDaysMin);
  const [daysMaxInput, setDaysMaxInput] = useState(settledDaysMax);
  const updateParams = useCallback(
    (updates: Record<string, string | number | null>, resetPage = true) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value === null || value === '') next.delete(key);
          else next.set(key, String(value));
        }
        if (resetPage) next.delete('page');
        return next;
      });
    },
    [setSearchParams]
  );
  const debouncedSetSearchParam = useDebounce(
    useCallback(
      (value: string) => {
        updateParams({ q: value }, true);
      },
      [updateParams]
    ),
    300
  );
  const debouncedSetDayRangeParams = useDebounce(
    useCallback(
      (daysMin: string, daysMax: string) => {
        updateParams(
          {
            days_min: formatDaysInput(parseDaysInput(daysMin)),
            days_max: formatDaysInput(parseDaysInput(daysMax)),
          },
          true
        );
      },
      [updateParams]
    ),
    300
  );

  useEffect(() => {
    debouncedSetSearchParam.cancel();
    setSearchInput(settledQ);
  }, [debouncedSetSearchParam, settledQ]);

  useEffect(() => {
    debouncedSetDayRangeParams.cancel();
    setDaysMinInput(settledDaysMin);
  }, [debouncedSetDayRangeParams, settledDaysMin]);

  useEffect(() => {
    debouncedSetDayRangeParams.cancel();
    setDaysMaxInput(settledDaysMax);
  }, [debouncedSetDayRangeParams, settledDaysMax]);

  const sortBy = readSort(searchParams.get('sort')) ?? 'days_past_due';
  const sortDirection = readDirection(searchParams.get('dir')) ?? 'desc';
  const page = readPositiveIntegerParam(searchParams.get('page'), 1);
  const pageSize = coerceReportPageSize(
    readPositiveIntegerParam(searchParams.get('size'), 25)
  );
  const isSearchPending = searchInput !== settledQ;
  const isDayRangePending =
    daysMinInput !== settledDaysMin || daysMaxInput !== settledDaysMax;
  const isExportPending = isSearchPending || isDayRangePending;

  const query = useMemo(
    () => ({
      as_of: asOf || null,
      clinic_ids: readFilterQuery(clinic),
      categories: categoriesForTaskType(taskType),
      discipline_ids: readFilterQuery(discipline),
      assigned_clinician_ids: readFilterQuery(clinician),
      statuses: readFilterQuery(status),
      target_date_from: targetFrom || null,
      target_date_to: targetTo || null,
      days_past_due_min: settledDaysMin ? Number(settledDaysMin) : null,
      days_past_due_max: settledDaysMax ? Number(settledDaysMax) : null,
      q: settledQ || null,
      ...buildReportPopulationQuery({
        includeTestRows,
        includeInactiveEpisodes,
        includeImportedPatients,
      }),
      include_missed_visits: includeMissedVisits,
      sort_by: sortBy,
      sort_direction: sortDirection,
      page,
      page_size: pageSize,
    }),
    [
      asOf,
      clinic,
      taskType,
      discipline,
      clinician,
      status,
      targetFrom,
      targetTo,
      settledDaysMin,
      settledDaysMax,
      settledQ,
      includeTestRows,
      includeInactiveEpisodes,
      includeImportedPatients,
      includeMissedVisits,
      sortBy,
      sortDirection,
      page,
      pageSize,
    ]
  );

  useEffect(() => {
    if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) return;
    let cancelled = false;
    async function loadOptions() {
      try {
        const response =
          await getPastDueVisitsReportOptionsReportsPastDueVisitsOptionsGet({
            query: {
              as_of: asOf || null,
              ...buildReportPopulationQuery({
                includeTestRows,
                includeInactiveEpisodes,
                includeImportedPatients,
              }),
              include_missed_visits: includeMissedVisits,
            },
          });
        if (cancelled) return;
        if (response.error || !response.data) {
          console.error('Failed to load Past Due Tasks options', response);
          setError(
            getUserFacingApiMessage(
              response,
              'Unable to load Past Due Tasks. Please try again.'
            )
          );
          setLoading(false);
          return;
        }
        setOptions({
          asOf: response.data.as_of,
          clinics: response.data.clinics,
          disciplines: response.data.disciplines,
          assignedClinicians: response.data.assigned_clinicians,
          statuses: response.data.statuses,
        });
      } catch (caught) {
        if (!cancelled) {
          console.error('Failed to load Past Due Tasks options', caught);
          setError(
            getBusinessErrorMessage(caught) ??
              'Unable to load Past Due Tasks. Please try again.'
          );
          setLoading(false);
        }
      }
    }
    void loadOptions();
    return () => {
      cancelled = true;
    };
  }, [
    asOf,
    hasPermission,
    includeInactiveEpisodes,
    includeTestRows,
    includeImportedPatients,
    includeMissedVisits,
  ]);

  useEffect(() => {
    if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS) || !asOf)
      return;
    let cancelled = false;
    async function loadReport() {
      setLoading(true);
      setError(null);
      try {
        const response = await getPastDueVisitsReportReportsPastDueVisitsGet({
          query,
        });
        if (cancelled) return;
        if (response.error || !response.data) {
          console.error('Failed to load Past Due Tasks report', response);
          setResult(null);
          setError(
            getUserFacingApiMessage(
              response,
              'Unable to load Past Due Tasks. Please try again.'
            )
          );
          return;
        }
        setResult(response.data);
      } catch (caught) {
        if (cancelled) return;
        console.error('Failed to load Past Due Tasks report', caught);
        setResult(null);
        setError(
          getBusinessErrorMessage(caught) ??
            'Unable to load Past Due Tasks. Please try again.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void loadReport();
    return () => {
      cancelled = true;
    };
  }, [asOf, hasPermission, query]);

  const handleSortChange = (nextSort: ReportTableSort | null) => {
    const clearedDefaultSort =
      nextSort === null &&
      sortBy === 'days_past_due' &&
      sortDirection === 'desc';
    updateParams(
      {
        sort: nextSort?.id ?? 'days_past_due',
        dir: nextSort?.direction ?? (clearedDefaultSort ? 'asc' : 'desc'),
      },
      true
    );
  };

  const handlePaginationChange = (next: ReportTablePaginationState) => {
    updateParams({ page: next.page, size: next.pageSize }, false);
  };

  const clearFilters = () => {
    debouncedSetSearchParam.cancel();
    debouncedSetDayRangeParams.cancel();
    setSearchInput('');
    setDaysMinInput('');
    setDaysMaxInput('');
    setSearchParams(new URLSearchParams());
  };

  const handleExport = async () => {
    if (isExportPending) return;
    setExporting(true);
    try {
      const exportQuery = {
        as_of: query.as_of,
        clinic_ids: query.clinic_ids,
        categories: query.categories,
        discipline_ids: query.discipline_ids,
        assigned_clinician_ids: query.assigned_clinician_ids,
        statuses: query.statuses,
        target_date_from: query.target_date_from,
        target_date_to: query.target_date_to,
        days_past_due_min: query.days_past_due_min,
        days_past_due_max: query.days_past_due_max,
        q: query.q,
        include_test_rows: query.include_test_rows,
        include_inactive_episodes: query.include_inactive_episodes,
        include_imported_patients: query.include_imported_patients,
        include_missed_visits: query.include_missed_visits,
        sort_by: query.sort_by,
        sort_direction: query.sort_direction,
      };
      const response =
        await exportPastDueVisitsReportReportsPastDueVisitsExportGet({
          query: exportQuery,
          responseType: 'blob',
        });
      if (response.error || !response.data) {
        console.error('Failed to export Past Due Tasks report', response);
        showError(
          await getUserFacingApiMessageAsync(
            response,
            'Unable to export Past Due Tasks. Please try again.'
          )
        );
        return;
      }
      downloadResponseAsFile(response, `Roger_Past_Due_Tasks_${asOf}.xlsx`);
    } catch (caught) {
      console.error('Failed to export Past Due Tasks report', caught);
      showError(
        await getUserFacingApiMessageAsync(
          caught,
          'Unable to export Past Due Tasks. Please try again.'
        )
      );
    } finally {
      setExporting(false);
    }
  };

  if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) {
    return <ReportsPermissionAlert />;
  }

  const total = result?.total ?? 0;
  const pagination = {
    page,
    pageSize,
    pageSizeOptions: [...REPORT_PAGE_SIZE_OPTIONS],
    total,
    totalPages: Math.max(1, Math.ceil(total / pageSize)),
  };

  return (
    <div className='space-y-6'>
      <Link
        to={reportsBackHref}
        className='inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground'
      >
        <ArrowLeft className='mr-1 h-4 w-4' /> Reports
      </Link>
      <div className='flex flex-wrap items-start gap-3'>
        <div className='flex flex-wrap items-center gap-2'>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={handleExport}
            disabled={exporting || loading || isExportPending}
          >
            <Download className='h-4 w-4' />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
          <ReportPopulationControls
            includeTestRows={includeTestRows}
            includeInactiveEpisodes={includeInactiveEpisodes}
            includeImportedPatients={includeImportedPatients}
            onIncludeTestRowsChange={checked =>
              updateParams({ include_test_rows: checked ? 'true' : null })
            }
            onIncludeInactiveEpisodesChange={checked =>
              updateParams({
                include_inactive_episodes: checked ? 'true' : null,
              })
            }
            onIncludeImportedPatientsChange={checked => {
              setStoredIncludeImportedPatients(checked);
              updateParams({ include_imported_patients: String(checked) });
            }}
            includeMissedVisits={includeMissedVisits}
            onIncludeMissedVisitsChange={checked =>
              updateParams({ include_missed_visits: checked ? null : 'false' })
            }
          />
        </div>
      </div>

      <section
        aria-label='Past Due Tasks filters'
        className='space-y-4 rounded-lg border bg-card p-4'
      >
        <div className='grid gap-3 md:grid-cols-3 lg:grid-cols-5'>
          <label className='space-y-1 text-sm font-medium'>
            As of
            <Input
              type='date'
              inputSize='default'
              value={asOf}
              max={options.asOf || undefined}
              onChange={event => updateParams({ as_of: event.target.value })}
            />
          </label>
          <ReportMultiSelectFilter
            label='Clinic'
            value={clinic}
            options={options.clinics}
            allLabel='All clinics'
            onChange={value => updateParams({ clinic: value })}
          />
          <label className='space-y-1 text-sm font-medium'>
            Task type
            <Select
              value={visibleTaskType}
              onValueChange={value =>
                updateParams({ category: value === 'both' ? null : value })
              }
            >
              <SelectTrigger selectSize='default'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='both'>All tasks</SelectItem>
                <SelectItem value='visit'>Visits</SelectItem>
                <SelectItem value='order'>Orders</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <ReportMultiSelectFilter
            label='Discipline'
            value={discipline}
            options={options.disciplines}
            allLabel='All disciplines'
            onChange={value => updateParams({ discipline: value })}
          />
          <ReportMultiSelectFilter
            label='Assigned clinician'
            value={clinician}
            options={options.assignedClinicians}
            allLabel='All clinicians'
            leadingOptions={[{ id: '__unassigned__', label: 'Unassigned' }]}
            deferApply
            onChange={value => updateParams({ clinician: value })}
          />
          <StatusFilter
            value={status}
            options={options.statuses}
            onChange={value => updateParams({ status: value })}
          />
          <label className='space-y-1 text-sm font-medium'>
            Target from
            <Input
              type='date'
              inputSize='default'
              value={targetFrom}
              onChange={event =>
                updateParams({ target_from: event.target.value })
              }
            />
          </label>
          <label className='space-y-1 text-sm font-medium'>
            Target to
            <Input
              type='date'
              inputSize='default'
              value={targetTo}
              onChange={event =>
                updateParams({ target_to: event.target.value })
              }
            />
          </label>
          <label className='space-y-1 text-sm font-medium'>
            Days min
            <Input
              type='number'
              min={1}
              step={1}
              inputMode='numeric'
              inputSize='default'
              value={daysMinInput}
              onChange={event => {
                const nextMin = formatDaysInput(
                  parseDaysInput(event.target.value)
                );
                const currentMax = parseDaysInput(daysMaxInput);
                const nextMax =
                  nextMin && currentMax !== null && Number(nextMin) > currentMax
                    ? nextMin
                    : daysMaxInput;
                setDaysMinInput(nextMin);
                setDaysMaxInput(nextMax);
                debouncedSetDayRangeParams(nextMin, nextMax);
              }}
            />
          </label>
          <label className='space-y-1 text-sm font-medium'>
            Days max
            <Input
              type='number'
              min={1}
              step={1}
              inputMode='numeric'
              inputSize='default'
              value={daysMaxInput}
              onChange={event => {
                const nextMax = formatDaysInput(
                  parseDaysInput(event.target.value)
                );
                const currentMin = parseDaysInput(daysMinInput);
                const nextMin =
                  nextMax && currentMin !== null && Number(nextMax) < currentMin
                    ? nextMax
                    : daysMinInput;
                setDaysMinInput(nextMin);
                setDaysMaxInput(nextMax);
                debouncedSetDayRangeParams(nextMin, nextMax);
              }}
            />
          </label>
          <label className='space-y-1 text-sm font-medium md:col-span-2'>
            Search
            <Input
              inputSize='default'
              value={searchInput}
              placeholder='Search patient, MRN, clinic, or task'
              onChange={event => {
                const next = event.target.value;
                setSearchInput(next);
                debouncedSetSearchParam(next);
              }}
            />
          </label>
          <div className='flex items-end'>
            <Button type='button' variant='outline' onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </div>
      </section>

      {error ? (
        <Alert variant='destructive'>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {!loading && result?.items.length === 0 ? (
        <EmptyState
          title='No past due tasks found'
          description='Try adjusting the filters or as-of date.'
        />
      ) : result || loading ? (
        <div className='overflow-x-auto'>
          <ReportTable
            caption='Past Due Tasks report results'
            columns={columns}
            data={result?.items ?? []}
            getRowId={row => row.task_id}
            isLoading={loading}
            sorting={{ id: sortBy, direction: sortDirection }}
            onSortingChange={handleSortChange}
            pagination={pagination}
            onPaginationChange={handlePaginationChange}
          />
        </div>
      ) : null}
    </div>
  );
}
